import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import BackToTop from "@/components/feature/BackToTop";
import ScoopCard from "@/components/base/ScoopCard";
import VerdictBadge from "@/components/base/VerdictBadge";
import CredibilityMeter from "@/components/base/CredibilityMeter";
import { useSeo } from "@/hooks/useSeo";
import { scoops } from "@/mocks/scoops";
import { verdictMeta } from "@/mocks/types";
import EvidenceList from "./components/EvidenceList";
import Timeline from "./components/Timeline";
import SidePanel from "./components/SidePanel";

export default function ScoopDetailPage() {
  const { id } = useParams<{ id: string }>();
  const scoop = scoops.find((item) => item.id === id);

  const jsonLd = useMemo(() => {
    if (!scoop) return undefined;
    return [
      {
        "@type": "Article",
        headline: scoop.title,
        description: scoop.summary,
        image: scoop.cover,
        datePublished: scoop.date,
        dateModified: scoop.updated.slice(0, 10),
        inLanguage: "zh-CN",
        articleSection: scoop.category,
        keywords: scoop.tags.join(","),
        author: { "@type": "Organization", name: "拾真社" },
        publisher: { "@type": "Organization", name: "拾真社" },
        mainEntityOfPage: `https://hdlyjgs.cn/scoop/${scoop.id}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://hdlyjgs.cn/" },
          { "@type": "ListItem", position: 2, name: "爆料库", item: "https://hdlyjgs.cn/scoops" },
          { "@type": "ListItem", position: 3, name: scoop.title, item: `https://hdlyjgs.cn/scoop/${scoop.id}` },
        ],
      },
    ];
  }, [scoop]);

  useSeo({
    title: scoop ? `${scoop.title}｜独家爆料求证档案 · 拾真社` : "档案不存在｜拾真社",
    description: scoop
      ? `${scoop.summary} 本档案评级：${verdictMeta[scoop.verdict].label}，可信度 ${scoop.credibility}/100，含 ${scoop.evidence.length} 份原始材料与来源链。`
      : "未找到你访问的求证档案，可返回爆料库查看全部已求证内容。",
    keywords: scoop
      ? `独家爆料,${scoop.category},爆料求证,实锤证据,${scoop.tags.join(",")}`
      : "独家爆料,爆料求证",
    path: scoop ? `/scoop/${scoop.id}` : "/scoops",
    jsonLd,
  });

  if (!scoop) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader activePath="/scoops" />
        <Breadcrumbs items={[{ label: "爆料库", to: "/scoops" }, { label: "档案不存在" }]} />
        <main className="mx-auto flex max-w-[1200px] flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这份档案</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            可能是链接已更新，或者该档案尚未公开。你可以回到爆料库搜索其他档案号。
          </p>
          <Link
            to="/scoops"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回爆料库
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = scoops.filter((item) => scoop.related.includes(item.id)).slice(0, 3);
  const fallbackRelated = scoops.filter((item) => item.id !== scoop.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader activePath="/scoops" />
      <Breadcrumbs items={[{ label: "爆料库", to: "/scoops" }, { label: scoop.code.replace("档案号 ", "") }]} />

      <section className="border-b border-background-200 bg-foreground-950">
        <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-8 md:py-14">
          <div className="flex flex-wrap items-center gap-3">
            <VerdictBadge verdict={scoop.verdict} />
            <span className="font-label text-[12px] text-background-300">{scoop.code}</span>
            <span className="font-label text-[12px] text-accent-400">{scoop.category}</span>
          </div>
          <h1 className="mt-5 max-w-4xl font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
            {scoop.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">{scoop.summary}</p>

          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 font-label text-[12px] text-background-300">
            <span className="whitespace-nowrap"><i className="ri-edit-2-line mr-1.5"></i>{scoop.author}</span>
            <span className="whitespace-nowrap"><i className="ri-calendar-line mr-1.5"></i>发布 {scoop.date}</span>
            <span className="whitespace-nowrap"><i className="ri-refresh-line mr-1.5"></i>更新 {scoop.updated}</span>
            <span className="whitespace-nowrap"><i className="ri-time-line mr-1.5"></i>{scoop.readMinutes} 分钟阅读</span>
            <span className="whitespace-nowrap"><i className="ri-links-line mr-1.5"></i>{scoop.sources} 个独立来源</span>
          </div>

          <div className="mt-7 max-w-sm [&_.font-label]:!text-background-300 [&_.text-foreground-950]:!text-background-50 [&_.text-foreground-400]:!text-background-400">
            <CredibilityMeter value={scoop.credibility} />
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[1200px] px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px] lg:gap-12">
          <article className="min-w-0">
            <figure className="overflow-hidden rounded-lg border border-background-200">
              <div className="relative w-full h-64 md:h-[380px]">
                <img
                  src={scoop.cover}
                  alt={`${scoop.title} 对应的证据材料整理配图`}
                  title={`${scoop.title} · 拾真社档案 ${scoop.code.replace("档案号 ", "")}`}
                  className="absolute inset-0 h-full w-full object-top object-cover"
                />
              </div>
              <figcaption className="border-t border-background-200 bg-background-100 px-4 py-2.5 font-label text-[11px] text-foreground-500">
                图为该档案相关材料的整理示意，非原始材料实拍。
              </figcaption>
            </figure>

            <section id="article-body" className="scroll-mt-24 pt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">求证正文</h2>
              <div className="mt-5 space-y-5">
                {scoop.body.map((paragraph, index) => (
                  <p key={index} className="text-[15px] leading-[1.9] text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {scoop.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-[12px] text-secondary-800">
                    #{tag}
                  </span>
                ))}
              </div>
            </section>

            <section id="article-evidence" className="scroll-mt-24 pt-12">
              <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">证据清单</h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    共 {scoop.evidence.length} 份材料，每一份都标注了核验状态。
                  </p>
                </div>
                <span className="font-label text-[11px] text-foreground-500 whitespace-nowrap">
                  {scoop.evidence.filter((item) => item.status === "verified").length} 已核验 ·{" "}
                  {scoop.evidence.filter((item) => item.status === "pending").length} 待核验
                </span>
              </div>
              <EvidenceList items={scoop.evidence} />
            </section>

            <section id="article-timeline" className="scroll-mt-24 pt-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件时间线</h2>
              <p className="mt-2 mb-6 text-sm text-foreground-600">
                按时间顺序梳理材料出现与核查进展，最新更新在最下方。
              </p>
              <Timeline items={scoop.timeline} />
            </section>

            <section id="article-related" className="scroll-mt-24 pt-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关档案</h2>
              <p className="mt-2 mb-6 text-sm text-foreground-600">同一领域或存在关联的其他求证档案。</p>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                {relatedList.map((item) => (
                  <ScoopCard key={item.id} scoop={item} />
                ))}
              </div>
            </section>

            <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">关于本档案的说明</h2>
              <p className="mt-3 text-[13px] leading-relaxed text-foreground-600">
                本档案中所有涉及个人的信息均已做脱敏处理。档案结论基于撰写时可获得的材料，
                如出现新的原始材料，我们会重新开档核实并更新评级。引用本档案内容时，请同时注明可信度评分与更新时间，避免脱离语境传播。
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to="/verify"
                  className="inline-flex items-center gap-2 rounded-md border border-foreground-300 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
                >
                  <i className="ri-question-answer-line" aria-hidden="true"></i>
                  查看评级标准
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-md border border-foreground-300 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
                >
                  <i className="ri-flag-line" aria-hidden="true"></i>
                  报告错误
                </Link>
              </div>
            </div>
          </article>

          <SidePanel scoop={scoop} />
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}